import { useCallback, useEffect, useRef, useState } from "react";
import { createLogger } from "@/logging";
import { isAbortError } from "@/utils";
import { resolveLoadErrorMessage, type LoadState } from "../loadState";
import {
  loadEpisodeDiagnosticDetail,
  loadEpisodePhaseGridFor,
  type EpisodeDiagnosticDetail,
  type EpisodePhaseGridResult,
} from "./diagnosticsSeries";

const logger = createLogger("components/dataset/diagnostics");

export type EpisodeDiagnosticDetailState = {
  // 1번째 RTT. 상태와 채널 사실을 가진다. null 은 404(부재·가시성)다.
  detail: LoadState<EpisodeDiagnosticDetail | null>;
  // 2번째 RTT. 위상 그리드다. 상세가 오기 전에는 loading 이다.
  grid: LoadState<EpisodePhaseGridResult>;
};

const LOADING: EpisodeDiagnosticDetailState = {
  detail: { status: "loading" },
  grid: { status: "loading" },
};

/**
 * 선택한 에피소드의 진단 상세와 위상 그리드를 두 단계로 로드한다.
 *
 * 상세가 도착하면 그리드를 기다리지 않고 상세를 먼저 반환한다. 채널 사실은 첫 응답에 이미 있다.
 * 에피소드가 바뀌면 진행 중인 두 요청을 모두 끊고 늦게 온 결과는 버린다. 그래서 ↑/↓ 를 빠르게
 * 눌러도 이전 에피소드의 응답이 화면을 덮지 않는다. 그리드가 실패하면 그리드만 오류로 두고
 * 상세는 유지한다.
 *
 * 두 단계는 스위치가 따로 있다. `enabled` 는 상세를 켜고, `gridEnabled` 는 그리드를 켠다.
 * 그래서 상세만 쓰는 자리(리뷰 탭의 action_source 칩)는 그리드 아티팩트를 받지 않는다.
 * 그리드를 나중에 켜면 받아 둔 상세는 그대로 두고 그리드만 받는다. 상세를 다시 요청하지 않는다.
 *
 * `refreshToken` 이 바뀌면 받아 둔 상세만 제자리에서 다시 받는다. 로딩 상태로 비우지 않고 그리드도
 * 다시 받지 않는다. 검토 완료가 품질 후보 확인을 기록한 뒤 쓴다. `reload` 는 둘 다 처음부터 받는다.
 */
export function useEpisodeDiagnosticDetail(
  datasetUuid: string,
  episodeUuid: string,
  enabled = true,
  gridEnabled = enabled,
  refreshToken = 0,
): EpisodeDiagnosticDetailState & { reload: () => void } {
  // 상태에 에피소드 키를 붙인다. 에피소드를 바꾼 첫 렌더는 effect 가 상태를 비우기 전이다.
  // 키 없이 반환하면 이전 에피소드의 구간이 새 에피소드 화면에 한 번 보인다.
  const [revision, setRevision] = useState(0);
  const reload = useCallback(() => setRevision((value) => value + 1), []);
  const key = `${datasetUuid}/${episodeUuid}/${revision}`;
  const [keyed, setKeyed] = useState<{ key: string; state: EpisodeDiagnosticDetailState }>({
    key,
    state: LOADING,
  });
  const state = keyed.key === key ? keyed.state : LOADING;

  // 이 키로 끝까지 받은 단계. 스위치를 껐다 켜도 이미 받은 것을 다시 요청하지 않는다.
  const detailDone = useRef<string | null>(null);
  const gridDone = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || detailDone.current === key) return;
    detailDone.current = null;
    gridDone.current = null;
    const controller = new AbortController();
    const { signal } = controller;
    setKeyed({ key, state: LOADING });

    async function load(): Promise<void> {
      try {
        const detail = await loadEpisodeDiagnosticDetail(datasetUuid, episodeUuid, { signal });
        if (signal.aborted) return;
        detailDone.current = key;
        setKeyed({
          key,
          state: { detail: { status: "success", data: detail }, grid: { status: "loading" } },
        });
      } catch (error) {
        if (signal.aborted || isAbortError(error)) return;
        logger.error("loadEpisodeDiagnosticDetail failed", error);
        const failed = { status: "error", message: resolveLoadErrorMessage(error) } as const;
        setKeyed({ key, state: { detail: failed, grid: failed } });
      }
    }

    void load();
    return () => controller.abort();
  }, [datasetUuid, episodeUuid, key, enabled]);

  // 이 키에서 마지막으로 처리한 refreshToken. 키가 바뀌면 그때의 값을 기준으로 삼는다. 그래서
  // 에피소드를 옮긴 것만으로는 다시 받지 않는다.
  const handledToken = useRef({ key, token: refreshToken });
  useEffect(() => {
    const handled = handledToken.current;
    if (handled.key !== key || handled.token === refreshToken) {
      handledToken.current = { key, token: refreshToken };
      return;
    }
    handledToken.current = { key, token: refreshToken };
    // 상세를 아직 받지 않았으면 진행 중인 첫 요청이 최신 값을 가져온다.
    if (!enabled || detailDone.current !== key) return;
    const controller = new AbortController();
    const { signal } = controller;

    async function refresh(): Promise<void> {
      try {
        const detail = await loadEpisodeDiagnosticDetail(datasetUuid, episodeUuid, { signal });
        if (signal.aborted) return;
        setKeyed((current) =>
          current.key === key
            ? { key, state: { ...current.state, detail: { status: "success", data: detail } } }
            : current,
        );
      } catch (error) {
        if (signal.aborted || isAbortError(error)) return;
        // 받아 둔 상세를 그대로 둔다. 화면이 비는 것보다 조금 낡은 확인 표시가 낫다.
        logger.error("loadEpisodeDiagnosticDetail refresh failed", error);
      }
    }

    void refresh();
    return () => controller.abort();
  }, [datasetUuid, episodeUuid, key, enabled, refreshToken]);

  // 그리드는 상세가 이 키로 도착한 뒤에만 시작한다. 상세가 없으면(404) 그리드도 unavailable 이다.
  const detailReady = state.detail.status === "success";
  const detailData = state.detail.status === "success" ? state.detail.data : null;
  useEffect(() => {
    if (!gridEnabled || !detailReady || gridDone.current === key) return;
    const controller = new AbortController();
    const { signal } = controller;
    const setGrid = (grid: EpisodeDiagnosticDetailState["grid"]): void =>
      setKeyed((current) =>
        current.key === key ? { key, state: { ...current.state, grid } } : current,
      );
    setGrid({ status: "loading" });

    async function load(): Promise<void> {
      try {
        const grid = await loadEpisodePhaseGridFor(detailData, { signal });
        if (signal.aborted) return;
        gridDone.current = key;
        setGrid({ status: "success", data: grid });
      } catch (error) {
        if (signal.aborted || isAbortError(error)) return;
        logger.error("loadEpisodePhaseGridFor failed", error);
        setGrid({ status: "error", message: resolveLoadErrorMessage(error) });
      }
    }

    void load();
    return () => controller.abort();
  }, [key, gridEnabled, detailReady, detailData]);

  return { ...state, reload };
}
